import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clientQuery } from 'Core/data/GraphQLApi';
import { endpoints } from 'Core/defaultValues';
import { ExceptionManager } from 'Core/logManager';
import { catalogLoad } from 'Core/catalogs/redux/actions';

const route = 'core/hooks/useUnitInputs';

/**
 * GraphQL query for fetching per-vehicle input label assignments
 * Uses searchUnitInputs API from px-cloud
 */
const SEARCH_UNIT_INPUTS_QUERY = `
    query SearchUnitInputs($unitId: Int!) {
        data: searchUnitInputs(unitId: $unitId) {
            unitId
            position
            labelId
            onLabel
            offLabel
        }
    }
`;

/**
 * Default input labels for fallback when no custom labels are configured
 */
const DEFAULT_INPUT_LABELS = [
    { position: 1, onLabel: 'Input 1 On', offLabel: 'Input 1 Off' },
    { position: 2, onLabel: 'Input 2 On', offLabel: 'Input 2 Off' },
    { position: 3, onLabel: 'Input 3 On', offLabel: 'Input 3 Off' },
    { position: 4, onLabel: 'Input 4 On', offLabel: 'Input 4 Off' },
    { position: 5, onLabel: 'Input 5 On', offLabel: 'Input 5 Off' },
];

/**
 * Custom hook to fetch per-vehicle input labels
 * 
 * @param {number|null} unitId - The vehicle ID to fetch inputs for
 * @returns {Object} { unitInputs, loading, error, refetch, getInputLabel }
 * 
 * @example
 * const { unitInputs, loading, getInputLabel } = useUnitInputs(12345);
 * // Get label for position 1
 * const label = getInputLabel(1); // Returns "Door Open" or "Input 1 On" as fallback
 */
const useUnitInputs = (unitId) => {
    const dispatch = useDispatch();
    const inputLabels = useSelector(state => state.catalogsRedux.inputLabels);
    const [unitInputs, setUnitInputs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    /**
     * Load inputLabels catalog (contains title for each label)
     */
    useEffect(() => {
        dispatch(catalogLoad(['inputLabels']));
    }, [dispatch]);

    /**
     * Fetch unit inputs from API
     */
    const fetchUnitInputs = useCallback(async () => {
        if (!unitId) {
            setUnitInputs([]);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const response = await clientQuery(
                SEARCH_UNIT_INPUTS_QUERY,
                { unitId: parseInt(unitId, 10) },
                endpoints.GRAPHQL_GENERAL
            );

            if (response?.data) {
                setUnitInputs(response.data);
            } else {
                setUnitInputs([]);
            }
        } catch (exc) {
            ExceptionManager(exc, route, 'fetchUnitInputs');
            setError(exc);
            setUnitInputs([]);
        } finally {
            setLoading(false);
        }
    }, [unitId]);

    /**
     * Load unit inputs when unitId changes
     */
    useEffect(() => {
        fetchUnitInputs();
    }, [fetchUnitInputs]);

    /**
     * Enrich unitInputs with title from inputLabels catalog
     */
    const enrichedUnitInputs = useMemo(() => {
        if (!unitInputs.length) return unitInputs;
        if (!inputLabels?.length) return unitInputs;

        return unitInputs.map(input => {
            const catalogLabel = inputLabels.find(l => l.id === input.labelId);
            return {
                ...input,
                title: catalogLabel?.title || null
            };
        });
    }, [unitInputs, inputLabels]);

    /**
     * Build a position → label mapping for quick lookup
     */
    const labelMap = useMemo(() => {
        const map = {};
        enrichedUnitInputs.forEach(input => {
            map[input.position] = {
                labelId: input.labelId,
                onLabel: input.onLabel,
                offLabel: input.offLabel,
                title: input.title
            };
        });
        return map;
    }, [enrichedUnitInputs]);

    /**
     * Get the label for a specific input position
     * Falls back to default "Input X On/Off" if not configured
     * 
     * @param {number} position - Input position (1-5)
     * @param {string} state - 'on' or 'off' (default: 'on')
     * @returns {string} The label text
     */
    const getInputLabel = useCallback((position, state = 'on') => {
        const input = labelMap[position];
        if (input) {
            return state === 'on' ? input.onLabel : input.offLabel;
        }
        // Fallback to default
        const defaultInput = DEFAULT_INPUT_LABELS.find(d => d.position === position);
        return state === 'on' 
            ? (defaultInput?.onLabel || `Input ${position} On`)
            : (defaultInput?.offLabel || `Input ${position} Off`);
    }, [labelMap]);

    /**
     * Get all input options for dropdowns (with fallback for unconfigured positions)
     * Returns array of { position, onLabel, offLabel } for all 5 inputs
     */
    const allInputOptions = useMemo(() => {
        return DEFAULT_INPUT_LABELS.map(defaultInput => {
            const customInput = labelMap[defaultInput.position];
            return {
                position: defaultInput.position,
                onLabel: customInput?.onLabel || defaultInput.onLabel,
                offLabel: customInput?.offLabel || defaultInput.offLabel,
                labelId: customInput?.labelId || null,
                isCustom: !!customInput
            };
        });
    }, [labelMap]);

    return {
        unitInputs: enrichedUnitInputs,
        loading,
        error,
        refetch: fetchUnitInputs,
        getInputLabel,
        labelMap,
        allInputOptions
    };
};

export default useUnitInputs;

/**
 * GraphQL query for standalone function (without alias)
 */
const SEARCH_UNIT_INPUTS_QUERY_STANDALONE = `
    query SearchUnitInputs($unitId: Int!) {
        searchUnitInputs(unitId: $unitId) {
            unitId
            position
            labelId
            onLabel
            offLabel
        }
    }
`;

/**
 * Standalone function to fetch unit inputs (for use outside React components)
 * 
 * @param {number} unitId - The vehicle ID
 * @returns {Promise<Array>} Array of unit inputs
 */
export const fetchUnitInputsAsync = async (unitId) => {
    if (!unitId) return [];

    try {
        const response = await clientQuery(
            SEARCH_UNIT_INPUTS_QUERY_STANDALONE,
            { unitId: parseInt(unitId, 10) },
            endpoints.GRAPHQL_GENERAL
        );
        return response?.searchUnitInputs || [];
    } catch (exc) {
        ExceptionManager(exc, route, 'fetchUnitInputsAsync');
        return [];
    }
};
